import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { UserSearch, Plus, Trash2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

/** Genesys agent name → Kippu user mapping so spelling differences never
 *  block auto-assignment on call log import. */
export default function AgentAliasPanel() {
  const { activeSlug } = useClient();
  const { hasPerm } = useAuth();
  const canManage = hasPerm("leads.import_call_logs");
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [alias, setAlias] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/lead-agent-aliases").then((r) => setItems(r.data.items || [])).catch(() => {});
    api.get("/leads/assignable-users").then((r) => setUsers(r.data.users || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load, activeSlug]);

  const add = async () => {
    if (!alias.trim() || !email) return;
    setBusy(true);
    try {
      await api.post("/lead-agent-aliases", { alias: alias.trim(), user_email: email });
      toast.success(`Alias "${alias.trim()}" mapped`);
      setAlias(""); setEmail("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save alias");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (it) => {
    try {
      await api.delete(`/lead-agent-aliases/${it.id}`);
      toast.success(`Alias "${it.alias}" removed`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete alias");
    }
  };

  return (
    <Card data-testid="agent-alias-panel">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <UserSearch className="w-4 h-4 text-slate-500" /> Agent Alias Mapping
        </CardTitle>
        <p className="text-[11px] text-slate-400 font-normal">
          Map agent names exactly as they appear in the Genesys file to Kippu users. Aliases are
          checked first during import, so spelling differences never block auto-assignment.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {canManage && (
        <div className="flex flex-wrap items-center gap-2">
          <Input value={alias} onChange={(e) => setAlias(e.target.value)}
                 placeholder="Genesys agent name (e.g. Ankit Kumar)"
                 className="h-8 text-xs w-64" data-testid="alias-name-input" />
          <ArrowRight className="w-3.5 h-3.5 text-slate-400" />
          <Select value={email} onValueChange={setEmail}>
            <SelectTrigger className="h-8 text-xs w-72" data-testid="alias-user-select">
              <SelectValue placeholder="Kippu user" />
            </SelectTrigger>
            <SelectContent className="max-h-64">
              {users.map((u) => (
                <SelectItem key={u.email} value={u.email}>
                  {u.name} · {u.email}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" className="h-8 bg-indigo-600 hover:bg-indigo-700"
                  disabled={busy || !alias.trim() || !email} onClick={add}
                  data-testid="alias-add-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add mapping
          </Button>
        </div>
        )}
        {items.length === 0 ? (
          <div className="text-xs text-slate-400 border border-dashed border-slate-200 rounded px-3 py-4 text-center">
            No aliases yet. Unmatched agent names from imports appear in the error report — map them here.
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase text-slate-400">
              <tr>
                <th className="text-left px-2 py-1">Genesys name</th>
                <th className="text-left px-2">Kippu user</th>
                <th className="text-left px-2">Added by</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-t border-slate-100" data-testid={`alias-row-${it.id}`}>
                  <td className="px-2 py-1.5 font-medium text-slate-800">{it.alias}</td>
                  <td className="px-2 text-slate-600">
                    {it.user_name} <span className="text-slate-400">· {it.user_email}</span>
                  </td>
                  <td className="px-2 text-[10px] text-slate-400">{it.updated_by}</td>
                  <td className="px-2 text-right">
                    {canManage && (
                      <Button variant="ghost" size="icon" className="h-6 w-6 text-rose-500 hover:text-rose-700"
                              onClick={() => remove(it)} data-testid={`alias-delete-${it.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}
